import React from "react";
import type { StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { OptionTs } from "@ps-bridge/interop";
import type { ClientsViewProps } from "@ps-bridge/contracts/clients";
import type { Ok } from "@ps-bridge/ok";
import type { ProjectDocument } from "@ps-bridge/models/core/dataContracts";
import * as IO from "@ps-bridge/interop/io";
import DocumentsPanel from "@view/panels/workspace/clients/DocumentsPanel.panel";
import type { SupplyStatusResponse } from "@services/clients/DocumentAssistantSupplyApi";

type ContractDocumentItem =
  ClientsViewProps extends { documents: Array<infer Item> } ? Item : never;
type ContractViewDocument = ClientsViewProps extends {
  onViewDocument: infer Handler;
}
  ? Handler
  : never;
type ContractDownloadDocument = ClientsViewProps extends {
  onDownloadDocument: infer Handler;
}
  ? Handler
  : never;

type DocumentsPanelViewProps = {
  activeClientName: string;
  isUploadPanelOpen: boolean;
  uploadPanelView: React.ReactElement;
  hasActiveProject: boolean;
  supplyStatusRows: SupplyStatusResponse[];
  supplySelectedFileName: string;
  supplyErrorMessage: string;
  isSupplySubmitting: boolean;
  onSelectSupplyFile: (fileList: OptionTs<FileList>) => Promise<Ok>;
  onRefreshSupplyStatus: (supplyId: string) => Promise<Ok>;
  documentQuery: string;
  onDocumentQueryChange: StateUpdate<string>;
  documentNameOptions: string[];
  emptyQueueView: React.ReactElement;
  emptyDocumentsView: React.ReactElement;
  filteredDocuments: ContractDocumentItem[];
  onViewDocument: ContractViewDocument;
  onDownloadDocument: ContractDownloadDocument;
  onRemoveDocument: (document: ProjectDocument) => Ok;
  runIO: typeof IO.run;
  formatBytes: (value: number) => string;
  formatTime: (value: string) => string;
};

const DocumentsPanelView: React.FC<DocumentsPanelViewProps> = ({
  activeClientName,
  isUploadPanelOpen,
  uploadPanelView,
  hasActiveProject,
  supplyStatusRows,
  supplySelectedFileName,
  supplyErrorMessage,
  isSupplySubmitting,
  onSelectSupplyFile,
  onRefreshSupplyStatus,
  documentQuery,
  onDocumentQueryChange,
  documentNameOptions,
  emptyQueueView,
  emptyDocumentsView,
  filteredDocuments,
  onViewDocument,
  onDownloadDocument,
  onRemoveDocument,
  runIO,
  formatBytes,
  formatTime,
}) => {
  const clientLabel = activeClientName ? `Client: ${activeClientName}` : "";

  return (
    <DocumentsPanel
      clientLabel={clientLabel}
      uploadPanelView={uploadPanelView}
      isUploadPanelOpen={isUploadPanelOpen}
      hasActiveProject={hasActiveProject}
      supplyStatusRows={supplyStatusRows}
      supplySelectedFileName={supplySelectedFileName}
      supplyErrorMessage={supplyErrorMessage}
      isSupplySubmitting={isSupplySubmitting}
      onSelectSupplyFile={onSelectSupplyFile}
      onRefreshSupplyStatus={onRefreshSupplyStatus}
      documentQuery={documentQuery}
      onDocumentQueryChange={onDocumentQueryChange}
      documentNameOptions={documentNameOptions}
      emptyQueueView={emptyQueueView}
      emptyDocumentsView={emptyDocumentsView}
      filteredDocuments={filteredDocuments}
      onViewDocument={onViewDocument}
      onDownloadDocument={onDownloadDocument}
      onRemoveDocument={onRemoveDocument}
      runIO={runIO}
      formatBytes={formatBytes}
      formatTime={formatTime}
    />
  );
};

export default DocumentsPanelView;
